import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Button, Tooltip } from 'antd';
import { 
  DashboardOutlined,
  ProjectOutlined,
  ToolOutlined,
  CalculatorOutlined,
  BarChartOutlined,
  TeamOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyCertificateOutlined,
  ContainerOutlined
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Sidebar.css';

const { Sider } = Layout;

const Sidebar = ({ collapsed, onCollapse, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedKey, setSelectedKey] = useState('dashboard');

  useEffect(() => {
    const path = location.pathname.split('/')[1] || 'dashboard';
    setSelectedKey(path);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      path: '/dashboard'
    },
    {
      key: 'planning',
      icon: <ProjectOutlined />,
      label: 'Mine Planning',
      path: '/planning',
      roles: ['admin', 'manager']
    },
    {
      key: 'operations',
      icon: <ToolOutlined />,
      label: 'Operations',
      path: '/operations',
      roles: ['admin', 'supervisor', 'operator']
    },
    {
      key: 'costing',
      icon: <CalculatorOutlined />,
      label: 'Cost Analysis',
      path: '/cost-analysis',
      roles: ['admin', 'manager']
    },
    {
      key: 'equipment',
      icon: <ContainerOutlined />,
      label: 'Equipment',
      path: '/equipment',
      roles: ['admin', 'manager', 'maintenance']
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: 'Reports',
      path: '/reports',
      roles: ['admin', 'manager', 'supervisor']
    },
    {
      key: 'personnel',
      icon: <TeamOutlined />,
      label: 'Personnel',
      path: '/personnel',
      roles: ['admin', 'manager']
    },
    {
      key: 'safety',
      icon: <SafetyCertificateOutlined />,
      label: 'Safety',
      path: '/safety',
      roles: ['admin', 'supervisor', 'operator']
    }
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  });

  // Convert menu items to antd format
  const items = [
    ...filteredMenuItems.map(item => ({
      key: item.key,
      icon: <span className="menu-item-icon">{item.icon}</span>,
      label: (
        <Link to={item.path}>
          <span className={`menu-item-text ${collapsed ? 'menu-item-text-hidden' : ''}`}>
            {item.label}
          </span>
          {selectedKey === item.key && <div className="active-dot" />}
          {item.key === 'operations' && (
            <span className={`menu-badge ${collapsed ? 'menu-badge-collapsed' : ''}`}>
              3
            </span>
          )}
        </Link>
      ),
      className: `menu-item ${selectedKey === item.key ? 'menu-item-selected' : ''}`
    })),
    { type: 'divider' },
    ...(user?.role === 'admin' || user?.role === 'manager' ? [{
      key: 'settings',
      icon: <SettingOutlined className="menu-item-icon" />,
      label: (
        <Link to="/settings">
          <span className={`menu-item-text ${collapsed ? 'menu-item-text-hidden' : ''}`}>
            Settings
          </span>
        </Link>
      ),
      className: `menu-item ${selectedKey === 'settings' ? 'menu-item-selected' : ''}`
    }] : [])
  ];

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username;
  };

  const getUserRoleDisplay = () => {
    if (!user) return 'Guest';
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  };

  const sidebarClass = `
    sidebar 
    ${collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}
    ${mobileOpen ? 'sidebar-mobile-open' : ''}
  `;

  return (
    <>
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={onMobileClose} />
      )}
      
      <Sider
        className={sidebarClass}
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={256}
        breakpoint="lg"
        onBreakpoint={onCollapse}
      >
        <div className={`sidebar-logo ${collapsed ? 'sidebar-logo-collapsed' : ''}`}>
          <div className="logo-container">
            <div className="logo-image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="white"/>
                <path d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={`logo-text ${collapsed ? 'logo-text-hidden' : ''}`}>
              MinePlan Pro
            </div>
          </div>
        </div>

        <Menu
          className="sidebar-menu"
          mode="inline"
          selectedKeys={[selectedKey]}
          theme="dark"
          items={items}
        />

        <div className={`user-info ${collapsed ? 'user-info-collapsed' : ''}`}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar className="user-avatar" size="large">
              {getInitials(getUserDisplayName())}
            </Avatar>
            <div className={`user-details ${collapsed ? 'user-details-hidden' : ''}`}>
              <div className="user-name">{getUserDisplayName()}</div>
              <div className="user-role">{getUserRoleDisplay()}</div>
            </div>
            <Tooltip title="Logout">
              <Button
                type="text"
                className="logout-button"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                size="small"
              />
            </Tooltip>
          </div>
        </div>

        {!mobileOpen && (
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => onCollapse(!collapsed)}
            style={{
              position: 'absolute',
              bottom: 80,
              right: -12,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: '#001529',
              color: 'white',
              border: '2px solid #002140',
              zIndex: 1001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        )}
      </Sider>
    </>
  );
};

export default Sidebar;